/* eslint-disable camelcase */
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { parseJwt } from '@minecraft.utils';
import { getAccessToken } from '@blocs.auth';
import { gqlExternalAuthenticationProvider } from '@minecraft.graphql-types';
import { PaywallLocation } from '../Paywall/enums/PaywallLocation';
import { getTrialTimeLeft } from '../Paywall/helpers/trialDuration';
import { publishEvent } from './publish-event';
import { MediaPurchase } from '../Paywall/enums/plans';

type PurchaseFlowEvent =
  | 'purchase'
  | 'begin_checkout'
  | 'view_item_list'
  | 'select_item'
  | 'a_la_carte_media_purchase_media'
  | 'purchase_success';

interface PurchaseItem {
  code: string;
  name?: string;
  price?: number;
  trialEndsAt?: string;
  currency?: {
    code: string;
  };
}

interface EventPurchase {
  items?: readonly PurchaseItem[];
  subscriptions?: readonly PurchaseItem[];
  currency?: {
    code: string;
  };
  discount?: number;
  creditsApplied?: number;
  total?: number;
  couponCodes?: readonly string[];
}
interface PurchaseFlowData {
  purchase?: EventPurchase;
  location?: PaywallLocation;
  transactionId?: string;
  currentPlan?: string;
}

export function getItems(args: { location?: PaywallLocation; purchase?: EventPurchase }) {
  const { location, purchase } = args;

  if (!purchase) return [];

  const dataLayerItems =
    purchase?.items?.map((item) => {
      return omitBy(
        {
          item_id: item.code,
          item_name: item.name ?? item.code,
          item_list_id: location,
          item_category: 'item',
          currency: item.currency?.code ?? purchase?.currency?.code,
          price: item.price,
          discount: Number(purchase.discount || 0) + Number(purchase.creditsApplied || 0),
        },
        isNil
      );
    }) || [];

  const dataLayerSubscriptions =
    purchase?.subscriptions?.map((subscription) => {
      return omitBy(
        {
          item_id: `${subscription.code}${subscription.trialEndsAt ? '_trial' : ''}`,
          item_name: subscription.name,
          item_list_id: location,
          item_category: 'plan',
          currency: subscription.currency?.code ?? purchase?.currency?.code,
          price: subscription.price,
          discount: Number(purchase.discount || 0) + Number(purchase.creditsApplied || 0),
          trial_days: getTrialTimeLeft(subscription.trialEndsAt)?.days ?? 0,
        },
        isNil
      );
    }) || [];

  return [...dataLayerItems, ...dataLayerSubscriptions];
}

export function publishPurchaseFlowEvent(
  event: PurchaseFlowEvent,
  { purchase, location, transactionId, currentPlan }: PurchaseFlowData
) {
  const isGoogleSSO =
    parseJwt(getAccessToken())?.loginAttributes?.externalAuthProvider === gqlExternalAuthenticationProvider.GOOGLE;
  const eventPayload = omitBy(
    {
      method: isGoogleSSO ? 'google_sso' : 'email',
      current_plan: currentPlan,
      ecommerce: omitBy(
        {
          transaction_id: transactionId,
          items: getItems({ location, purchase }),
          item_list_id: location,
          currency: purchase?.currency?.code,
          value: purchase?.total,
          coupon: purchase?.couponCodes?.join(','),
        },
        isNil
      ),
    },
    isNil
  );
  const isAlacartePurchase = purchase?.items?.some((item) => item.code === MediaPurchase.alacarte);
  if (isAlacartePurchase) {
    publishEvent({ event, ...eventPayload, location });
  } else {
    publishEvent({ event, ...eventPayload });
  }
}
